import React, { useEffect, useState, useCallback, useRef } from "react";
import { api } from "../../lib/api";
import { apiErr } from "../../lib/utils";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { Upload, Trash2, Film, RefreshCw } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function PlatformTraining() {
  const [modules, setModules] = useState([]);
  const [busy, setBusy] = useState("");
  const fileRefs = useRef({});

  const load = useCallback(() => {
    api.get("/training/walkthroughs").then((r) => setModules(r.data.modules || [])).catch((e) => toast.error(apiErr(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const onPick = (key) => fileRefs.current[key]?.click();

  const onUpload = async (key, file) => {
    if (!file) return;
    setBusy(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/training/walkthroughs/${key}/upload`, fd);
      toast.success("Walkthrough updated");
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  const onReset = async (key) => {
    setBusy(key);
    try { await api.delete(`/training/walkthroughs/${key}`); toast.success("Cleared"); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  return (
    <div className="space-y-4" data-testid="platform-training">
      <p className="text-sm text-muted-foreground max-w-3xl">
        Short silent walkthroughs shown inside each training module on the in-app Guide. Auto-generated previews are in place — upload a polished screen recording (webp/gif/mp4, max 25&nbsp;MB) to replace any of them. Clips load lazily and are served from object storage.
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {modules.map((m) => (
          <div key={m.key} className="card-flat p-3 flex gap-3" data-testid={`training-row-${m.key}`}>
            <div className="w-40 shrink-0 rounded-sm overflow-hidden border border-border bg-black/20 grid place-items-center" style={{ aspectRatio: "16/10" }}>
              {m.has_media ? (
                (m.media_type || "").startsWith("video")
                  ? <video src={`${BACKEND}/api/training/media/${m.media_path}`} muted loop autoPlay playsInline className="w-full h-full object-cover" />
                  : <img src={`${BACKEND}/api/training/media/${m.media_path}`} alt="" className="w-full h-full object-cover" data-testid={`training-preview-${m.key}`} />
              ) : <Film className="h-6 w-6 text-muted-foreground" />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-medium text-sm truncate">{m.label}</div>
              <div className="text-[11px] text-muted-foreground font-mono">{m.route}</div>
              <div className="mt-1 flex items-center gap-1.5">
                <Badge variant={m.source === "upload" ? "success" : m.source === "auto" ? "warning" : "outline"} className="text-[9px]">
                  {m.source === "upload" ? "Custom upload" : m.source === "auto" ? "Auto preview" : "No clip"}
                </Badge>
                {m.media_type && <span className="text-[10px] text-muted-foreground">{m.media_type}</span>}
              </div>
              <div className="mt-2 flex items-center gap-2">
                <input ref={(el) => (fileRefs.current[m.key] = el)} type="file" accept="image/webp,image/gif,image/png,image/jpeg,video/mp4,video/webm"
                  className="hidden" data-testid={`training-file-${m.key}`} onChange={(e) => onUpload(m.key, e.target.files?.[0])} />
                <Button size="sm" variant="primary" disabled={busy === m.key} onClick={() => onPick(m.key)} data-testid={`training-upload-${m.key}`}>
                  <Upload className="h-3.5 w-3.5" /> {busy === m.key ? "Uploading…" : (m.has_media ? "Replace" : "Upload")}
                </Button>
                {m.has_media && <Button size="sm" variant="ghost" disabled={busy === m.key} onClick={() => onReset(m.key)} data-testid={`training-reset-${m.key}`}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>}
              </div>
            </div>
          </div>
        ))}
      </div>
      <Button size="sm" variant="outline" onClick={load}><RefreshCw className="h-3.5 w-3.5" /> Refresh</Button>
    </div>
  );
}
